
// InstallmentDetails.jsx — Updated with Cash Payment Option
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import './InstallmentDetails.css';
import { useAuth } from "../../AuthContext";
import ArrowBack from '@mui/icons-material/ArrowBack';
import PaymentSummary from './PaymentSummary';
import configData from '../../configure';
import { getOrgUpiId } from '../../config/orgUpiConfig';

function InstallmentDetails({ 
  selectedInstallment, 
  totalFee, 
  selectedUser, 
  onBack, 
  feeStructureMeta, 
  onPaymentComplete 
}) {
  const { axios, token, user } = useAuth(); // Assuming useAuth provides current logged-in user for createdBy

  const [paymentComplete, setPaymentComplete] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const [paymentMode, setPaymentMode] = useState(null);
  const [paymentTime, setPaymentTime] = useState(null);

  const [openCashDialog, setOpenCashDialog] = useState(false);
  const [receiptNumber, setReceiptNumber] = useState('');
  const [remarks, setRemarks] = useState('');

  const [openUpiDialog, setOpenUpiDialog] = useState(false);
  const [upiTxnId, setUpiTxnId] = useState('');
  const [upiReceiptNo, setUpiReceiptNo] = useState('');
  const [upiRemarks, setUpiRemarks] = useState('');
  const [loading, setLoading] = useState(false);

  // ─── Helper: Send ERP Inbox notification to student after payment ─────────────
  const sendPaymentSuccessMessage = async (amount, installmentDesc, mode, txnId) => {
    try {
      const paymentDate = new Date().toLocaleString('en-IN', {
        day: 'numeric', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
      const targetUserId = selectedUser?.user?.userId;
      const orgId = feeStructureMeta?.orgId;
      if (!targetUserId || !orgId) return;

      const subject = `Fee Payment Successful - ₹${amount.toLocaleString('en-IN')}`;
      const body =
        `Dear Student,\n\n` +
        `Your fee payment has been successfully processed. Here are the details:\n\n` +
        `📋 Installment : ${installmentDesc}\n` +
        `💰 Amount Paid : ₹${amount.toLocaleString('en-IN')}\n` +
        `🏦 Payment Mode: ${mode}\n` +
        `🔖 Transaction : ${txnId}\n` +
        `📅 Date & Time : ${paymentDate}\n\n` +
        `Please keep this message for your records. Download your receipt from the Fee section.\n\n` +
        `Regards,\nSchool Administration`;

      await axios.post(
        configData.INBOX_SEND,
        {
          subject,
          body,
          conversationId: null,
          orgId,
          targetType: 'INDIVIDUAL',
          targetId: targetUserId,
        },
        {
          headers: { Authorization: token },
          params: { userId: user?.userId || targetUserId }, // sender = logged-in admin/user
        }
      );
    } catch (err) {
      // Non-critical — don't block user flow if message fails
      console.warn('Failed to send payment inbox message:', err);
    }
  };

  const feeBreakdown = {
    tuitionFee: selectedInstallment.feeBreakdown?.['Tuition Fee'] || 0,
    libraryFee: selectedInstallment.feeBreakdown?.['Library Fee'] || 0,
    sportsFee: selectedInstallment.feeBreakdown?.['Sports Fee'] || 0,
    smartClassFee: selectedInstallment.feeBreakdown?.['SmartClass Fee'] || 0,
    computerFee: selectedInstallment.feeBreakdown?.['Computer Fee'] || 0,
    admissionFee: selectedInstallment.feeBreakdown?.['Admission Fee'] || 0,
    examinationFee: selectedInstallment.feeBreakdown?.['Examination Fee'] || 0,
    extracurricularFee: selectedInstallment.feeBreakdown?.['Extracurricular Fee'] || 0,
  };

  const calculatedTotalFee = Object.values(feeBreakdown).reduce((a, b) => a + b, 0);
  const displayTotalFee = totalFee || calculatedTotalFee;

  // Map to match API expected keys (based on your original feeBreakdownPaid example)
  const feeBreakdownPaid = {
    "Tuition Fee": feeBreakdown.tuitionFee,
    "Library Fee": feeBreakdown.libraryFee,
    "Sports Fee": feeBreakdown.sportsFee,
    "SmartClass Fee": feeBreakdown.smartClassFee,
    "Computer Fee": feeBreakdown.computerFee,
    "Admission Fee": feeBreakdown.admissionFee,
    "Examination Fee": feeBreakdown.examinationFee,
    "Extracurricular Fee": feeBreakdown.extracurricularFee,
  };

  const effectiveUserId = selectedUser?.studentInfo?.userId || selectedUser?.user?.userId || selectedUser?.userId || selectedUser?.id || user?.userId || user?.id;
  const effectiveOrgId = feeStructureMeta?.orgId || selectedUser?.orgId || selectedUser?.user?.orgId || user?.orgId;
  const effectiveAcademicYear = feeStructureMeta?.academicYear || selectedInstallment?.academicYear || '2025-2026';
  const activeOrgUpiId = getOrgUpiId(effectiveOrgId);

  const handleCashPayment = async () => {
    if (!receiptNumber.trim()) {
      toast.warning("Please enter a receipt number");
      return;
    }

    setLoading(true);
    try {
      const now = new Date().toISOString();

      const payload = {
        userId: effectiveUserId,
        studentId: effectiveUserId,
        installmentId: selectedInstallment?.installmentId || 'ANNUAL_TOTAL',
        installmentDescription: selectedInstallment?.description || 'Fee Installment',
        feeStructureId: feeStructureMeta?.feeStructureId,
        amountPaid: displayTotalFee,
        feeBreakdownPaid,
        lateFeeApplied: 0,
        orgId: effectiveOrgId,
        academicYear: effectiveAcademicYear,
        paymentMode: "CASH",
        currency: "INR",
        remarks: remarks || "Paid in cash",
        remainingAmount: 0,
        receiptNumber: receiptNumber.trim(),
        transactionId: "", // optional
        paymentDate: now,
        markAsSuccess: true,
        partialPayment: true,
        createdBy: user?.username || user?.email || "admin",
        createdAt: now,
        lastUpdatedBy: user?.username || user?.email || "admin",
        lastUpdatedAt: now,
        paymentMetaData: {}
      };

      const { data } = await axios.post(
        configData.INFRASTRUCTURE_FEEPAYMENT,
        payload,
        { headers: { Authorization: token, 'Content-Type': 'application/json' } }
      );

      if (data?.paymentStatus === "SUCCESS" || data?.status === "SUCCESS" || data?.receiptNumber) {
        setTransactionId(data.receiptNumber || receiptNumber);
        setPaymentMode("CASH");
        setPaymentTime(new Date(data.paymentDate || now).toLocaleString('en-IN'));
        setPaymentComplete(true);
        onPaymentComplete?.();
        sendPaymentSuccessMessage(
          displayTotalFee,
          selectedInstallment?.description || 'Fee Installment',
          'CASH',
          data.receiptNumber || receiptNumber
        );
      } else {
        toast.error("Cash payment recording failed");
      }
    } catch (error) {
      console.error("Cash payment failed:", error?.response?.data || error);
      toast.error(error?.response?.data?.message || "Failed to record cash payment. Please try again.");
    } finally {
      setLoading(false);
      setOpenCashDialog(false);
    }
  };

  const handleCopyUpi = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(activeOrgUpiId);
      toast.info(`UPI ID copied: ${activeOrgUpiId}`);
    }
  };

  const sendPendingUpiAdminMessage = async (amount, installmentDesc, txnId) => {
    try {
      const paymentDate = new Date().toLocaleString('en-IN', {
        day: 'numeric', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
      const studentName = `${selectedUser?.user?.firstName || ''} ${selectedUser?.user?.lastName || ''}`.trim() || 'Student';
      const studentUserId = selectedUser?.user?.userId || selectedUser?.userId || user?.userId;
      const orgId = effectiveOrgId;

      const subject = `⚠️ Fee Approval Request: UPI Payment - UTR: ${txnId} (₹${amount.toLocaleString('en-IN')})`;
      const body =
        `Dear Admin,\n\n` +
        `A new Direct UPI fee payment approval request has arrived for your verification:\n\n` +
        `👤 Student Name : ${studentName} (ID: ${studentUserId})\n` +
        `📋 Installment  : ${installmentDesc}\n` +
        `💰 Amount Paid  : ₹${amount.toLocaleString('en-IN')}\n` +
        `🔖 UTR Reference: ${txnId}\n` +
        `🏦 Paid To UPI  : ${activeOrgUpiId}\n` +
        `📅 Date & Time  : ${paymentDate}\n\n` +
        `Please verify this UTR against your ${activeOrgUpiId} bank statement and click Approve or Reject under the Student Fee Details section.\n\n` +
        `Regards,\neGurukul Fee Management System`;

      const targetTypes = ['ALL_STAFF', 'ALL'];
      for (const tType of targetTypes) {
        try {
          await axios.post(
            configData.INBOX_SEND,
            {
              subject,
              body,
              conversationId: null,
              orgId,
              targetType: tType,
              targetId: null,
              category: 'ALERT'
            },
            {
              headers: { Authorization: token },
              params: { userId: studentUserId },
            }
          );
        } catch (singleErr) {
          console.warn(`Failed to send approval request to ${tType}:`, singleErr);
        }
      }
    } catch (err) {
      console.warn('Failed to send pending UPI admin inbox message:', err);
    }
  };

  const handleUpiPayment = async () => {
    if (!upiTxnId.trim()) {
      toast.warning("Please enter your 12-digit UTR / Transaction Reference Number");
      return;
    }

    setLoading(true);
    try {
      const now = new Date().toISOString();
      const finalReceipt = upiReceiptNo.trim() || `UPI-${upiTxnId.trim()}`;
      
      const userRole = (user?.userType || user?.role || user?.userRole || '').toUpperCase();
      const isAdmin = userRole === 'ADMIN' || userRole === 'SUPERADMIN' || userRole === 'STAFF' || userRole === 'FACULTY' || userRole === 'ROLE_ADMIN';

      // For students, submit as PENDING_VERIFICATION requiring admin approval
      const isAutoSuccess = isAdmin;
      const initialStatus = isAutoSuccess ? "SUCCESS" : "PENDING_VERIFICATION";

      const payload = {
        userId: effectiveUserId,
        studentId: effectiveUserId,
        installmentId: selectedInstallment?.installmentId || 'ANNUAL_TOTAL',
        installmentDescription: selectedInstallment?.description || 'Fee Installment',
        feeStructureId: feeStructureMeta?.feeStructureId,
        amountPaid: displayTotalFee,
        feeBreakdownPaid,
        lateFeeApplied: 0,
        orgId: effectiveOrgId,
        academicYear: effectiveAcademicYear,
        paymentMode: "BANK_TRANSFER",
        paymentStatus: initialStatus,
        status: initialStatus,
        currency: "INR",
        remarks: upiRemarks || (isAutoSuccess ? `Direct UPI payment to ${activeOrgUpiId}` : `Direct UPI payment (UTR: ${upiTxnId.trim()}) - Pending Admin Verification`),
        remainingAmount: 0,
        receiptNumber: finalReceipt,
        transactionId: upiTxnId.trim(),
        paymentDate: now,
        markAsSuccess: isAutoSuccess,
        partialPayment: true,
        createdBy: user?.username || user?.email || (isAdmin ? "admin" : "student"),
        createdAt: now,
        lastUpdatedBy: user?.username || user?.email || (isAdmin ? "admin" : "student"),
        lastUpdatedAt: now,
        paymentMetaData: { upiId: activeOrgUpiId, utr: upiTxnId.trim(), isPendingApproval: !isAutoSuccess }
      };

      const { data } = await axios.post(
        configData.INFRASTRUCTURE_FEEPAYMENT,
        payload,
        { headers: { Authorization: token, 'Content-Type': 'application/json' } }
      );

      setTransactionId(upiTxnId.trim());
      setPaymentMode(isAutoSuccess ? "Direct UPI (Verified)" : "Direct UPI (Pending Verification)");
      setPaymentTime(new Date(data?.paymentDate || now).toLocaleString('en-IN'));
      setPaymentComplete(true);
      onPaymentComplete?.();

      if (isAutoSuccess) {
        toast.success("UPI Payment recorded & verified successfully!");
        sendPaymentSuccessMessage(
          displayTotalFee,
          selectedInstallment?.description || 'Fee Installment',
          'Direct UPI (Verified)',
          upiTxnId.trim()
        );
      } else {
        toast.info("UPI Payment UTR submitted! Pending Admin Verification.");
        sendPendingUpiAdminMessage(
          displayTotalFee,
          selectedInstallment?.description || 'Fee Installment',
          upiTxnId.trim()
        );
      }
    } catch (error) {
      console.error("UPI payment failed:", error?.response?.data || error);
      toast.error(error?.response?.data?.message || "Failed to submit UPI payment. Please try again.");
    } finally {
      setLoading(false);
      setOpenUpiDialog(false);
    }
  };

 const handlePayment = async (gateway) => {
    try {
      const payload = {
        userId: effectiveUserId,
        studentId: effectiveUserId,
        classId: selectedUser?.studentInfo?.classId || selectedUser?.classId || selectedUser?.user?.classId,
        feeStructureId: feeStructureMeta?.feeStructureId,
        installmentId: selectedInstallment?.installmentId || 'ANNUAL_TOTAL',
        amount: displayTotalFee,
        feeBreakdown,
        currency: "INR",
        orgId: effectiveOrgId,
        academicYear: effectiveAcademicYear,
        partialPayment: false,
        paymentGateway: gateway,
      };

      const { data } = await axios.post(configData.INITIATE_FEE_PAYMENT, payload, {
        headers: { Authorization: token }
      });

      // RAZORPAY
      if (gateway === "RAZORPAY" && data.razorpayOrderId) {
        const options = {
          key: 'rzp_live_bGEeLFK71r2jrJ',
          amount: displayTotalFee * 100,
          currency: 'INR',
          order_id: data.razorpayOrderId,
          name: `${selectedUser.user.firstName} ${selectedUser.user.lastName}`,
          description: selectedInstallment.description || "School Fee",
          handler: async (response) => {
            try {
              const verifyRes = await axios.post(configData.VERIFY_FEE_PAYMENT, {
                feePaymentId: data.feePaymentId,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpayOrderId: response.razorpay_order_id,
                razorpaySignature: response.razorpay_signature
              }, { headers: { Authorization: token } });

              if (verifyRes.data.paymentStatus === "SUCCESS") {
                setTransactionId(response.razorpay_payment_id);
                setPaymentMode(response.razorpay_payment_method || "Card/UPI");
                setPaymentTime(new Date().toLocaleString('en-IN'));
                setPaymentComplete(true);
                // Send ERP inbox notification to student
                sendPaymentSuccessMessage(
                  displayTotalFee,
                  selectedInstallment?.description || 'Fee Installment',
                  response.razorpay_payment_method || 'Card/UPI',
                  response.razorpay_payment_id
                );
              }
            } catch (err) {
              toast.error("Verification failed");
            }
          },
          prefill: {
            name: `${selectedUser.user.firstName} ${selectedUser.user.lastName}`,
            email: selectedUser.user.email || "",
            contact: selectedUser.user.phoneNumber || ""
          },
          theme: { color: "#3399cc" }
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      }

      // PAYPHI
      else if (gateway === "PAYPHI" && data.paymentUrl) {
        const payWindow = window.open(
          data.paymentUrl,
          'PayPhiPayment',
          'width=650,height=800'
        );

        if (!payWindow) {
          toast.warning("Please allow popups for PayPhi payment");
          return;
        }

        const check = setInterval(async () => {
          if (payWindow.closed) {
            clearInterval(check);
            try {
              const res = await axios.post(configData.VERIFY_FEE_PAYMENT, {
                payPhiMerchantTxnNo: data.payPhiMerchantTxnNo || data.merchantTxnNo,
                paymentGateway: "PAYPHI"
              }, { headers: { Authorization: token } });

              if (res.data.paymentStatus === "SUCCESS") {
                setTransactionId(res.data.payPhiMerchantTxnNo || res.data.merchantTxnNo);
                setPaymentMode(res.data.paymentMode || "UPI");
                setPaymentTime(new Date(res.data.paymentDate).toLocaleString('en-IN'));
                setPaymentComplete(true);
                // Send ERP inbox notification to student
                sendPaymentSuccessMessage(
                  displayTotalFee,
                  selectedInstallment?.description || 'Fee Installment',
                  res.data.paymentMode || 'UPI',
                  res.data.payPhiMerchantTxnNo || res.data.merchantTxnNo
                );
              }
            } catch (err) {
              console.log("PayPhi verification failed", err);
            }
          }
        }, 2000);
      }

    } catch (error) {
      console.error("Payment initiation failed:", error);
      toast.error("Failed to initiate payment. Please try again.");
    }
  };


  const handleBackToHome = () => {
    setPaymentComplete(false);
    onPaymentComplete?.();
    onBack?.();
  };

  if (paymentComplete) {
    return (
      <PaymentSummary
        selectedInstallment={selectedInstallment}
        totalFee={displayTotalFee}
        selectedUser={selectedUser}
        transactionId={transactionId}
        paymentMode={paymentMode}
        paymentTime={paymentTime}
        onBackToHome={handleBackToHome}
      />
    );
  }

  return (
    <div className='paymentWrapper'>
      <div className="backbutton">
        <Button onClick={onBack} startIcon={<ArrowBack />} />
      </div>

      <div className="paymentsummary">
        <h4 className="payment-title">Payment Details</h4>

        <div className="payment-row">
          <span className="payment-label">Amount</span>
          <span className="payment-value" style={{ fontWeight: 'bold', fontSize: '1.8rem', color: '#d32f2f' }}>
            ₹{displayTotalFee.toLocaleString('en-IN')}
          </span>
        </div>

        <div className="payment-row">
          <span className="payment-label">Name</span>
          <span className="payment-value">
            {selectedUser?.user?.firstName} {selectedUser?.user?.lastName}
          </span>
        </div>

        <div className="payment-row">
          <span className="payment-label">Email</span>
          <span className="payment-value">{selectedUser?.user?.email || "N/A"}</span>
        </div>

        <div className="payment-row">
          <span className="payment-label">Phone</span>
          <span className="payment-value">{selectedUser?.user?.phoneNumber || "N/A"}</span>
        </div>

        {/* TWO BUTTONS: Razorpay and Cash */}
        <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginTop: '20px', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => handlePayment("RAZORPAY")}
            sx={{
              background: 'linear-gradient(45deg, #FE6B8B, #FF8E53)',
              color: 'white',
              padding: '8px 16px',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              borderRadius: '12px',
              boxShadow: '0 6px 20px rgba(255,110,83,0.4)',
              '&:hover': { background: '#ff5a6e' }
            }}
          >
             Pay with Razorpay
          </Button>

          <Button
            variant="contained"
            size="small"
            onClick={() => setOpenCashDialog(true)}
            sx={{
              background: 'linear-gradient(45deg, #4caf50, #8bc34a)',
              color: 'white',
              padding: '8px 16px',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              borderRadius: '12px',
              boxShadow: '0 6px 20px rgba(76,175,80,0.4)',
              '&:hover': { background: '#43a047' }
            }}
          >
            Cash
          </Button>
        </div>
      </div>

      {/* Cash Payment Dialog */}
      <Dialog open={openCashDialog} onClose={() => setOpenCashDialog(false)}>
        <DialogTitle>Record Cash Payment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Receipt Number"
            fullWidth
            value={receiptNumber}
            onChange={(e) => setReceiptNumber(e.target.value)}
            required
          />
          <TextField
            margin="dense"
            label="Remarks (Optional)"
            fullWidth
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
          <p style={{ marginTop: '10px', fontSize: '0.9rem' }}>
            Amount: <strong>₹{displayTotalFee.toLocaleString('en-IN')}</strong><br />
            This will mark the installment as paid in CASH immediately.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCashDialog(false)}>Cancel</Button>
          <Button onClick={handleCashPayment} variant="contained" color="success" disabled={loading}>
            {loading ? 'Processing...' : 'Confirm Cash Payment'}
          </Button>
        </DialogActions>
      </Dialog>



    </div>
  );
}

export default InstallmentDetails;